import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "../components/ui/sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function StorePage() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [leads, setLeads] = useState([]);
  const [reservas, setReservas] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("config");
  const [openTooltip, setOpenTooltip] = useState(null);
  const [showEmbed, setShowEmbed] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [form, setForm] = useState({
    nombre_bot: "Kit",
    nombre_negocio: "",
    descripcion_negocio: "",
    sucursales: "",
    contacto_email: "",
    whatsapp_link: "",
    knowledge_base: "",
    notification_email: "",
    gmail_user: "",
    gmail_pass: "",
    openai_api_key: "",
    color_primario: "#0ea5e9",
    color_secundario: "#1d1d1b",
    color_texto: "#ffffff",
    mensaje_bienvenida: "",
    avatar_url: "",
    activo: true,
    plan: "trial",
    agenda_enabled: false,
    google_calendar_id: "",
    agenda_working_hours: "09:00-19:00",
    agenda_slot_duration: 60,
    agenda_notify_email: "",
    agenda_timezone: "America/Santiago",
  });

  useEffect(() => { fetchData(); }, []);

  async function fetchData() {
    setLoading(true);
    try {
      const [configRes, leadsRes, statsRes, reservasRes] = await Promise.all([
        fetch(`${API}/api/storebot/config`, { credentials: "include" }),
        fetch(`${API}/api/storebot/leads`, { credentials: "include" }),
        fetch(`${API}/api/storebot/leads/stats`, { credentials: "include" }),
        fetch(`${API}/api/storebot/reservas`, { credentials: "include" }),
      ]);
      const configData = await configRes.json();
      const leadsData = await leadsRes.json();
      const statsData = await statsRes.json();
      const reservasData = await reservasRes.json();
      if (configData.config) {
        setConfig(configData.config);
        setForm((f) => ({ ...f, ...configData.config }));
      }
      setLeads(leadsData.leads || []);
      setReservas(reservasData.reservas || []);
      setStats(statsData.stats || null);
    } catch (_e) {
      toast.error("Error cargando datos");
    }
    setLoading(false);
  }

  async function saveConfig() {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/storebot/config`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Configuración guardada");
        setConfig(data.config);
      } else {
        toast.error(data.detail || "Error al guardar");
      }
    } catch (_e) {
      toast.error("Error al guardar");
    }
    setSaving(false);
  }

  async function toggleBot() {
    try {
      const res = await fetch(`${API}/api/storebot/config/toggle`, {
        method: "PATCH",
        credentials: "include",
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.activo ? "StoreBot activado" : "StoreBot desactivado");
        fetchData();
      }
    } catch (_e) {
      toast.error("Error");
    }
  }

  // Public URL used by the embeddable widget snippet. Uses the config's
  // real Mongo _id — that's the opaque, rotatable identifier and matches
  // what the /api/storebot/chat endpoint expects.
  const configId = config?.id || "";
  const widgetUrl = configId ? `${API}/widget/storebot/${configId}` : "";
  const primaryColor = form.color_primario || "#0ea5e9";
  const secondaryColor = form.color_secundario || "#1d1d1b";
  const textColor = form.color_texto || "#ffffff";
  const welcomeMsg =
    (form.mensaje_bienvenida || "").trim() ||
    `Hola, soy ${form.nombre_bot || "el asistente"} de ${form.nombre_negocio || "la tienda"}. ¿En qué te ayudo?`;

  const embedSnippet = configId
    ? `<!-- StoreBot widget by ACBotHub -->
<script async src="${API}/storebot-widget.js"
        data-config="${configId}"
        data-api="${API}"
        data-color="${primaryColor}"
        data-color-secondary="${secondaryColor}"
        data-color-text="${textColor}"
        data-greeting="${welcomeMsg.replace(/"/g, "&quot;")}"></script>`
    : "";

  async function handleAvatarUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 350_000) {
      toast.error("La imagen debe pesar menos de 350 KB");
      return;
    }
    if (!file.type.startsWith("image/")) {
      toast.error("Solo se aceptan imágenes");
      return;
    }
    setUploadingAvatar(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const res = await fetch(`${API}/api/storebot/upload-avatar`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ data_url: dataUrl }),
      });
      const data = await res.json();
      if (data.success) {
        setForm((f) => ({ ...f, avatar_url: data.avatar_url }));
        toast.success("Avatar subido");
      } else {
        toast.error(data.detail || "Error al subir avatar");
      }
    } catch (_e) {
      toast.error("Error al procesar imagen");
    }
    setUploadingAvatar(false);
    // Reset input so uploading the same file again works.
    e.target.value = "";
  }

  const tipoBadge = (t) => {
    if (!t) return "bg-gray-100 text-gray-600";
    const s = t.toLowerCase();
    if (/(arquitect|dise|empresa|revend|fabric|profes)/.test(s)) return "bg-violet-100 text-violet-700";
    return "bg-sky-100 text-sky-700";
  };

  if (loading)
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600"></div>
      </div>
    );

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <button
        onClick={() => navigate("/dashboard")}
        data-testid="storebot-back-to-dashboard"
        className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-4 transition-colors"
      >
        ← Volver al Dashboard
      </button>

      {/* Widget CTA */}
      <div className="bg-sky-50 border border-sky-200 rounded-xl p-5 mb-6" data-testid="storebot-widget-cta">
        <div className="flex items-start gap-3">
          <div className="text-2xl">🌐</div>
          <div className="flex-1">
            <h3 className="text-base font-semibold text-sky-900 mb-1">Instala StoreBot en tu sitio web</h3>
            <p className="text-sm text-sky-800 mb-3">
              Copia el snippet HTML y pégalo antes del <code className="bg-white px-1 rounded text-xs">&lt;/body&gt;</code> en tu sitio. Se ve como un botón flotante estilo WhatsApp.
            </p>
            <button
              onClick={() => setShowEmbed((v) => !v)}
              data-testid="storebot-toggle-embed-btn"
              className="inline-flex items-center gap-2 bg-sky-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-sky-700 transition-colors text-sm"
            >
              {showEmbed ? "Ocultar snippet" : "Ver snippet de instalación"}
            </button>
            {showEmbed && (
              <div className="mt-3 bg-white border border-sky-200 rounded-lg p-3">
                <pre
                  data-testid="storebot-embed-snippet"
                  className="text-[11px] text-gray-700 whitespace-pre-wrap break-all font-mono"
                >
                  {embedSnippet || "Guarda la configuración primero para obtener el snippet."}
                </pre>
                {embedSnippet && (
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(embedSnippet);
                      toast.success("Snippet copiado al portapapeles");
                    }}
                    className="mt-2 text-xs text-sky-600 hover:text-sky-700 underline"
                    data-testid="storebot-copy-embed-btn"
                  >
                    📋 Copiar
                  </button>
                )}
                <p className="mt-3 text-xs text-gray-500">
                  ¿No sabes cómo pegarlo?{" "}
                  <a
                    href="/docs/storebot-widget"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sky-600 hover:underline"
                    data-testid="storebot-docs-link"
                  >
                    Ver guía de instalación (WordPress, Shopify, Wix) →
                  </a>
                </p>
                <p className="mt-2 text-xs text-gray-500">
                  También puedes probarlo directamente aquí:{" "}
                  {widgetUrl && (
                    <a
                      href={widgetUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sky-600 hover:underline"
                      data-testid="storebot-preview-link"
                    >
                      abrir vista previa →
                    </a>
                  )}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">🛍️ Kit StoreBot</h1>
          <p className="text-gray-500 text-sm mt-1">Asistente virtual para tiendas con catálogo</p>
        </div>
        {config && (
          <button
            onClick={toggleBot}
            data-testid="storebot-toggle-btn"
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              config.activo ? "bg-green-100 text-green-700 hover:bg-green-200" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            {config.activo ? "✅ Activo" : "⏸️ Inactivo"}
          </button>
        )}
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="storebot-stats">
          {[
            { label: "Total leads", value: stats.total ?? 0 },
            { label: "📅 Con cita", value: stats.con_cita ?? 0 },
            { label: "👔 Profesionales", value: stats.profesionales ?? 0 },
            { label: "🛒 Particulares", value: stats.particulares ?? 0 },
          ].map((s, i) => (
            <div key={i} className="bg-white rounded-xl border p-4 text-center">
              <div className="text-2xl font-bold text-gray-900">{s.value}</div>
              <div className="text-[11px] text-gray-500 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b">
        {["config", "agenda", "leads"].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`storebot-tab-${t}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t ? "border-sky-600 text-sky-600" : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {t === "config" ? "⚙️ Configuración" : t === "agenda" ? "📅 Agenda" : "👥 Leads"}
          </button>
        ))}
      </div>

      {/* Config Tab */}
      {tab === "config" && (
        <div className="bg-white rounded-xl border p-6 space-y-6" data-testid="storebot-config-panel">
          {/* Branding row — 3 color pickers + avatar upload */}
          <div className="space-y-4 pb-6 border-b">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="storebot-branding-colors">
              {[
                {
                  key: "color_primario",
                  label: "Color primario",
                  hint: "Botón flotante, halo, burbujas del cliente, botón enviar.",
                  fallback: "#0ea5e9",
                },
                {
                  key: "color_secundario",
                  label: "Color secundario",
                  hint: "Fondo del header del widget (barra superior con avatar y nombre). El área de mensajes es siempre gris cálido claro.",
                  fallback: "#1d1d1b",
                },
                {
                  key: "color_texto",
                  label: "Color del texto",
                  hint: "Color del texto sobre los fondos primario y secundario.",
                  fallback: "#ffffff",
                },
              ].map((c) => (
                <div key={c.key}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{c.label}</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={form[c.key] || c.fallback}
                      onChange={(e) => setForm((f) => ({ ...f, [c.key]: e.target.value }))}
                      data-testid={`storebot-input-${c.key}`}
                      className="w-12 h-11 rounded-md border border-gray-200 cursor-pointer shrink-0"
                    />
                    <input
                      type="text"
                      value={form[c.key] || c.fallback}
                      onChange={(e) => setForm((f) => ({ ...f, [c.key]: e.target.value }))}
                      className="flex-1 border rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-sky-500"
                      placeholder={c.fallback}
                    />
                  </div>
                  <p className="mt-1.5 text-[11px] text-gray-500 leading-relaxed">{c.hint}</p>
                </div>
              ))}
            </div>

            <div data-testid="storebot-branding-avatar">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Avatar del asistente
              </label>
              <div className="flex items-center gap-3">
                <div
                  className="w-14 h-14 rounded-full border-2 border-gray-200 shrink-0 overflow-hidden flex items-center justify-center text-2xl"
                  style={{ background: form.avatar_url ? "#fff" : "#f3f4f6" }}
                >
                  {form.avatar_url ? (
                    <img src={form.avatar_url} alt="Avatar" className="w-full h-full object-cover" data-testid="storebot-avatar-preview" />
                  ) : (
                    <span>🛍️</span>
                  )}
                </div>
                <label
                  className={`flex-1 inline-flex items-center justify-center gap-2 border-2 border-dashed rounded-lg px-3 py-2 text-sm cursor-pointer transition-colors ${
                    uploadingAvatar ? "opacity-60 cursor-wait" : "border-gray-200 hover:border-sky-400 hover:bg-sky-50 text-gray-600 hover:text-sky-700"
                  }`}
                >
                  {uploadingAvatar ? "Subiendo..." : (form.avatar_url ? "Reemplazar imagen" : "Subir imagen")}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarUpload}
                    className="hidden"
                    disabled={uploadingAvatar}
                    data-testid="storebot-avatar-input"
                  />
                </label>
              </div>
              <p className="mt-1.5 text-xs text-gray-500 leading-relaxed">
                PNG o JPG cuadrada, máx 350 KB. Se muestra en el header y en cada respuesta del bot.
              </p>
            </div>
          </div>

          {/* Preview widget button */}
          {configId && (
            <div className="flex items-center justify-between pb-4 border-b" data-testid="storebot-preview-row">
              <div>
                <div className="text-sm font-medium text-gray-900">Vista previa del widget</div>
                <div className="text-xs text-gray-500">Ver cómo se verá con los colores y avatar actuales.</div>
              </div>
              <button
                type="button"
                onClick={() => setShowPreview(true)}
                data-testid="storebot-open-preview-btn"
                className="text-sm font-medium text-sky-600 hover:text-sky-700 border border-sky-200 hover:border-sky-400 px-3 py-1.5 rounded-lg transition-colors"
              >
                👁 Ver preview
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { key: "nombre_bot", label: "Nombre del asistente", placeholder: "Sofía" },
              { key: "nombre_negocio", label: "Nombre del negocio", placeholder: "Casa Diseño Multiespacio" },
              {
                key: "descripcion_negocio",
                label: "Descripción del negocio",
                type: "textarea",
                rows: 3,
                placeholder: "2-3 líneas que expliquen qué venden y a quién.\nEj: Showroom de muebles y decoración de diseño en Santiago y Concepción. Atendemos arquitectos, diseñadores y particulares.",
                fullWidth: true,
              },
              {
                key: "sucursales",
                label: "Sucursales, dirección y horarios",
                type: "textarea",
                rows: 4,
                placeholder: "Ej:\nSantiago — Av. Providencia 1234, Lu-Vi 10:00-19:00, Sa 11:00-14:00. +56 9 1234 5678\nConcepción — Colo Colo 456, Lu-Vi 10:00-18:00. +56 9 8765 4321",
                helpText: "Texto libre. Puedes listar una o varias sucursales.",
                fullWidth: true,
              },
              { key: "contacto_email", label: "Email de contacto general", placeholder: "contacto@tunegocio.cl" },
              { key: "whatsapp_link", label: "Link de WhatsApp (wa.me)", placeholder: "https://wa.me/56912345678" },
              {
                key: "mensaje_bienvenida",
                label: "Mensaje de bienvenida del widget",
                type: "textarea",
                rows: 2,
                placeholder: "Ej: ¡Hola! Soy Sofía, la asistente virtual de Casa Diseño. ¿En qué puedo ayudarte hoy?",
                helpText: "Es el primer mensaje que ve el cliente al abrir el chat. Si lo dejas vacío, se genera uno automático con el nombre del bot y del negocio.",
                fullWidth: true,
              },
              {
                key: "knowledge_base",
                label: "Catálogo de productos (knowledge base)",
                type: "textarea",
                rows: 12,
                placeholder: `Pega aquí todo tu catálogo. Texto libre — el bot lo usa como fuente de verdad.

Ejemplo:
────────────────
SILLONES
- Sofá Escandinavo 3C  · $1.290.000 · Roble natural, tela beige · Stock: 4
- Sillón Bergere Cuero · $890.000  · Cuero negro · Stock: 2

MESAS
- Comedor Nogal 8p · $1.850.000 · Nogal macizo 220×95cm

ILUMINACIÓN
- Colgante Ámbar    · $189.000 · Latón + vidrio ámbar
- Lámpara de pie X  · $349.000 · Roble + lino natural

SERVICIOS
- Envío Santiago: gratis sobre $500.000
- Descuento arquitectos: 15% con factura
────────────────`,
                helpText: "Estructura libre. Mientras más completo el catálogo, mejores respuestas dará el bot.",
                fullWidth: true,
              },
              { key: "notification_email", label: "Email donde llegan los leads", placeholder: "leads@tunegocio.cl" },
              {
                key: "gmail_user",
                label: "Gmail para enviar notificaciones (opcional)",
                placeholder: "notificaciones@tunegocio.cl",
                helpText: "Si lo dejas vacío, usamos nuestro Gmail por defecto.",
              },
              {
                key: "gmail_pass",
                label: "Contraseña de app Gmail",
                placeholder: "xxxx xxxx xxxx xxxx",
                type: "password",
                help: {
                  title: "¿Cómo obtenerla?",
                  steps: [
                    { text: "Activa la Verificación en 2 pasos: ", link: { href: "https://myaccount.google.com/signinoptions/two-step-verification", label: "myaccount.google.com" } },
                    { text: "Ve a Contraseñas de aplicaciones: ", link: { href: "https://myaccount.google.com/apppasswords", label: "myaccount.google.com/apppasswords" } },
                    { text: 'En "Nombre de la app" escribe "StoreBot"' },
                    { text: 'Haz clic en "Crear"' },
                    { text: "Copia la contraseña de 16 caracteres y pégala aquí." },
                  ],
                },
              },
              {
                key: "openai_api_key",
                label: "OpenAI API Key",
                placeholder: "sk-proj-...",
                type: "password",
                help: {
                  title: "¿Cómo obtenerla?",
                  steps: [
                    { text: "Ve a ", link: { href: "https://platform.openai.com/api-keys", label: "platform.openai.com/api-keys" } },
                    { text: "Inicia sesión o crea una cuenta." },
                    { text: 'Haz clic en "Create new secret key".' },
                    { text: "Copia la key (empieza con sk-proj-)." },
                  ],
                  warning: "⚠️ Guárdala en un lugar seguro — no podrás verla nuevamente.",
                },
              },
            ].map(({ key, label, placeholder, type, rows, help, helpText, fullWidth }) => (
              <div key={key} className={fullWidth ? "md:col-span-2" : undefined}>
                <div className="flex items-center gap-1.5 mb-1">
                  <label className="block text-sm font-medium text-gray-700">{label}</label>
                  {help && (
                    <div className="relative inline-block group">
                      <button
                        type="button"
                        onClick={() => setOpenTooltip(openTooltip === key ? null : key)}
                        onBlur={() => setTimeout(() => setOpenTooltip((t) => (t === key ? null : t)), 150)}
                        aria-label={`Ayuda para ${label}`}
                        data-testid={`storebot-help-${key}`}
                        className="w-4 h-4 flex items-center justify-center rounded-full bg-gray-200 text-gray-600 text-[10px] font-bold hover:bg-sky-500 hover:text-white transition-colors cursor-help"
                      >
                        ?
                      </button>
                      <div
                        data-testid={`storebot-tooltip-${key}`}
                        className={`absolute z-30 left-0 top-6 w-72 sm:w-80 bg-white rounded-lg shadow-xl border border-gray-200 p-3 text-xs text-gray-700 transition-opacity duration-150 ${
                          openTooltip === key ? "opacity-100 visible" : "opacity-0 invisible group-hover:opacity-100 group-hover:visible"
                        }`}
                      >
                        <p className="font-semibold text-gray-900 mb-2">{help.title}</p>
                        <ol className="list-decimal list-inside space-y-1.5">
                          {help.steps.map((s, i) => (
                            <li key={i} className="leading-snug">
                              {s.text}
                              {s.link && (
                                <a href={s.link.href} target="_blank" rel="noopener noreferrer" className="text-sky-600 hover:underline break-all">
                                  {s.link.label}
                                </a>
                              )}
                            </li>
                          ))}
                        </ol>
                        {help.warning && (
                          <p className="mt-2 pt-2 border-t border-gray-100 text-amber-700">{help.warning}</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {type === "textarea" ? (
                  <textarea
                    rows={rows || 6}
                    value={form[key] || ""}
                    onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                    placeholder={placeholder}
                    data-testid={`storebot-input-${key}`}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 resize-y font-mono"
                  />
                ) : (
                  <input
                    type={type || "text"}
                    value={form[key] || ""}
                    onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                    placeholder={placeholder}
                    data-testid={`storebot-input-${key}`}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                  />
                )}
                {helpText && <p className="mt-1.5 text-xs text-gray-500 leading-relaxed">{helpText}</p>}
              </div>
            ))}
          </div>

          <button
            onClick={saveConfig}
            disabled={saving}
            data-testid="storebot-save-btn"
            className="w-full bg-sky-600 text-white py-2 rounded-lg font-medium hover:bg-sky-700 disabled:opacity-50 transition-colors"
          >
            {saving ? "Guardando..." : "Guardar configuración"}
          </button>
        </div>
      )}

      {/* Agenda Tab */}
      {tab === "agenda" && (
        <div className="space-y-6" data-testid="storebot-agenda-panel">
          <div className="bg-white rounded-xl border p-6 space-y-6">
            {/* Enable toggle */}
            <div className="flex items-center justify-between pb-4 border-b">
              <div>
                <div className="text-sm font-medium text-gray-900">Reservas con Google Calendar</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  El bot podrá agendar horas directamente en tu Google Calendar y evitará ofrecer horarios ya ocupados.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, agenda_enabled: !f.agenda_enabled }))}
                data-testid="storebot-agenda-enabled-toggle"
                className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors ${
                  form.agenda_enabled ? "bg-sky-600" : "bg-gray-300"
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform mt-0.5 ${
                    form.agenda_enabled ? "translate-x-5 ml-0.5" : "translate-x-0.5"
                  }`}
                />
              </button>
            </div>

            {!form.agenda_enabled ? (
              <div className="text-center py-6 text-gray-500 text-sm" data-testid="storebot-agenda-disabled-note">
                Activa el interruptor para configurar la agenda. Necesitarás un Google Calendar dedicado para las reservas.
              </div>
            ) : (
              <div className="space-y-5" data-testid="storebot-agenda-config">
                {/* Instructions */}
                <div className="bg-sky-50 border border-sky-200 rounded-lg p-4 text-sm" data-testid="storebot-agenda-instructions">
                  <p className="font-semibold text-sky-900 mb-2">📋 Conexión en 3 pasos:</p>
                  <ol className="list-decimal list-inside space-y-1.5 text-sky-900/80 text-xs leading-relaxed">
                    <li>Crea (o usa) un Google Calendar dedicado a las reservas de tu negocio.</li>
                    <li>
                      En Google Calendar → <b>Configuración del calendario</b> → <b>"Compartir con personas específicas"</b> → agrega:{" "}
                      <code className="bg-white border border-sky-200 rounded px-1.5 py-0.5 text-[11px] font-mono select-all" data-testid="storebot-agenda-sa-email">
                        {config?.agenda_sa_email || "cargando..."}
                      </code>{" "}
                      con permiso <b>"Hacer cambios en eventos"</b>.
                    </li>
                    <li>Copia el <b>ID del calendario</b> (en la misma sección de configuración, "Integrar calendario") y pégalo abajo.</li>
                  </ol>
                </div>

                {/* Calendar ID */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ID del Google Calendar</label>
                  <input
                    type="text"
                    value={form.google_calendar_id || ""}
                    onChange={(e) => setForm((f) => ({ ...f, google_calendar_id: e.target.value }))}
                    placeholder="xxxxxxxxxx@group.calendar.google.com"
                    data-testid="storebot-input-google_calendar_id"
                    className="w-full border rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-sky-500"
                  />
                  <p className="mt-1.5 text-xs text-gray-500">Sin este ID el bot no podrá crear eventos.</p>
                </div>

                {/* Working hours + slot duration + timezone */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Horario de atención</label>
                    <div className="flex items-center gap-2">
                      <select
                        value={(form.agenda_working_hours || "09:00-19:00").split("-")[0]}
                        onChange={(e) => {
                          const end = (form.agenda_working_hours || "09:00-19:00").split("-")[1] || "19:00";
                          setForm((f) => ({ ...f, agenda_working_hours: `${e.target.value}-${end}` }));
                        }}
                        data-testid="storebot-input-agenda-start"
                        className="flex-1 border rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                      >
                        {Array.from({ length: 18 }, (_, i) => `${String(i + 5).padStart(2, "0")}:00`).map((h) => (
                          <option key={h} value={h}>{h}</option>
                        ))}
                      </select>
                      <span className="text-gray-400 text-sm">a</span>
                      <select
                        value={(form.agenda_working_hours || "09:00-19:00").split("-")[1] || "19:00"}
                        onChange={(e) => {
                          const start = (form.agenda_working_hours || "09:00-19:00").split("-")[0] || "09:00";
                          setForm((f) => ({ ...f, agenda_working_hours: `${start}-${e.target.value}` }));
                        }}
                        data-testid="storebot-input-agenda-end"
                        className="flex-1 border rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                      >
                        {Array.from({ length: 18 }, (_, i) => `${String(i + 6).padStart(2, "0")}:00`).map((h) => (
                          <option key={h} value={h}>{h}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Duración por cita</label>
                    <select
                      value={form.agenda_slot_duration || 60}
                      onChange={(e) => setForm((f) => ({ ...f, agenda_slot_duration: parseInt(e.target.value, 10) }))}
                      data-testid="storebot-input-agenda_slot_duration"
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                    >
                      <option value={30}>30 minutos</option>
                      <option value={45}>45 minutos</option>
                      <option value={60}>1 hora</option>
                      <option value={90}>1,5 horas</option>
                      <option value={120}>2 horas</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Zona horaria</label>
                    <select
                      value={form.agenda_timezone || "America/Santiago"}
                      onChange={(e) => setForm((f) => ({ ...f, agenda_timezone: e.target.value }))}
                      data-testid="storebot-input-agenda_timezone"
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                    >
                      <option value="America/Santiago">Chile (Santiago)</option>
                      <option value="America/Argentina/Buenos_Aires">Argentina (Buenos Aires)</option>
                      <option value="America/Lima">Perú (Lima)</option>
                      <option value="America/Bogota">Colombia (Bogotá)</option>
                      <option value="America/Mexico_City">México (CDMX)</option>
                    </select>
                  </div>
                </div>

                {/* Notify email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email para notificaciones de reserva</label>
                  <input
                    type="email"
                    value={form.agenda_notify_email || ""}
                    onChange={(e) => setForm((f) => ({ ...f, agenda_notify_email: e.target.value }))}
                    placeholder={form.notification_email || "reservas@tunegocio.cl"}
                    data-testid="storebot-input-agenda_notify_email"
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                  />
                  <p className="mt-1.5 text-xs text-gray-500">Si lo dejas vacío, usamos el email de leads. Cada reserva confirmada llega aquí con el link al evento.</p>
                </div>

                <button
                  onClick={saveConfig}
                  disabled={saving}
                  data-testid="storebot-agenda-save-btn"
                  className="w-full bg-sky-600 text-white py-2 rounded-lg font-medium hover:bg-sky-700 disabled:opacity-50 transition-colors"
                >
                  {saving ? "Guardando..." : "Guardar agenda"}
                </button>
              </div>
            )}
          </div>

          {/* Reservations list */}
          <div className="bg-white rounded-xl border overflow-hidden" data-testid="storebot-reservas-panel">
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Reservas recientes</h3>
                <p className="text-xs text-gray-500 mt-0.5">Las que el bot ha agendado en tu Google Calendar.</p>
              </div>
              <span className="text-xs bg-sky-50 text-sky-700 border border-sky-200 rounded-full px-2.5 py-1 font-medium" data-testid="storebot-reservas-count">
                {reservas.length} total
              </span>
            </div>
            {reservas.length === 0 ? (
              <div className="p-8 text-center text-gray-500" data-testid="storebot-reservas-empty">
                <div className="text-4xl mb-2">📅</div>
                <p className="text-sm">Aún no hay reservas</p>
                <p className="text-xs mt-2 text-gray-400">Aparecerán aquí cuando un cliente agende desde el widget.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      {["Fecha", "Hora", "Nombre", "Teléfono", "Actividad", "Calendar"].map((h) => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {reservas.map((r, i) => (
                      <tr key={i} className="hover:bg-gray-50" data-testid={`storebot-reserva-row-${i}`}>
                        <td className="px-4 py-3 font-medium whitespace-nowrap">{r.fecha}</td>
                        <td className="px-4 py-3 text-gray-600">{r.hora}</td>
                        <td className="px-4 py-3">{r.nombre || "-"}</td>
                        <td className="px-4 py-3 text-gray-500">{r.telefono || "-"}</td>
                        <td className="px-4 py-3 text-gray-600">{r.actividad || "-"}</td>
                        <td className="px-4 py-3">
                          {r.event_link ? (
                            <a href={r.event_link} target="_blank" rel="noopener noreferrer" className="text-sky-600 hover:underline text-xs">
                              Ver evento
                            </a>
                          ) : "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Leads Tab */}
      {tab === "leads" && (
        <div className="bg-white rounded-xl border overflow-hidden" data-testid="storebot-leads-panel">
          {leads.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <div className="text-4xl mb-2">🛍️</div>
              <p>Aún no hay leads registrados</p>
              <p className="text-xs mt-2 text-gray-400">Los leads aparecen aquí cuando el bot captura nombre + teléfono + email de un cliente.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    {["Nombre", "Teléfono", "Email", "Producto", "Tipo", "Ciudad", "Visita", "Fecha captura"].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {leads.map((lead, i) => (
                    <tr key={i} className="hover:bg-gray-50" data-testid={`storebot-lead-row-${i}`}>
                      <td className="px-4 py-3 font-medium">{lead.nombre || "-"}</td>
                      <td className="px-4 py-3 text-gray-500">{lead.telefono || "-"}</td>
                      <td className="px-4 py-3 text-gray-500">{lead.email || "-"}</td>
                      <td className="px-4 py-3 text-gray-600">{lead.producto || "-"}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-[11px] font-medium ${tipoBadge(lead.tipo_cliente)}`}>
                          {lead.tipo_cliente || "-"}
                        </span>
                      </td>
                      <td className="px-4 py-3">{lead.ciudad || "-"}</td>
                      <td className="px-4 py-3 text-gray-500">{lead.fecha_visita || "-"}</td>
                      <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">
                        {lead.creado_en ? new Date(lead.creado_en).toLocaleString("es-CL", { dateStyle: "short", timeStyle: "short" }) : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {/* Preview widget modal */}
      {showPreview && configId && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => setShowPreview(false)}
          data-testid="storebot-preview-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md h-[600px] flex flex-col overflow-hidden relative"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
              <div>
                <div className="text-sm font-semibold text-gray-900">Preview en vivo</div>
                <div className="text-[11px] text-gray-500">Actualiza al guardar la configuración</div>
              </div>
              <button
                type="button"
                onClick={() => setShowPreview(false)}
                data-testid="storebot-close-preview-btn"
                className="w-8 h-8 rounded-full text-gray-500 hover:bg-gray-200 flex items-center justify-center transition-colors"
                aria-label="Cerrar preview"
              >
                ✕
              </button>
            </div>
            <iframe
              title="Widget preview"
              src={`${API}/widget/storebot/${configId}?color=${encodeURIComponent(primaryColor)}&color_secondary=${encodeURIComponent(secondaryColor)}&color_text=${encodeURIComponent(textColor)}&greeting=${encodeURIComponent(welcomeMsg)}`}
              className="flex-1 w-full border-0"
              data-testid="storebot-preview-iframe"
            />
          </div>
        </div>
      )}
    </div>
  );
}
